import React, { useState } from 'react';
import { ArrowLeft, Copy, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const HeartEmoji = () => {
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  const heartEmojis = {
    redHeart: '❤️',
    orangeHeart: '🧡',
    yellowHeart: '💛',
    greenHeart: '💚',
    blueHeart: '💙',
    purpleHeart: '💜',
    brownHeart: '🤎',
    blackHeart: '🖤',
    whiteHeart: '🤍',
    sparklingHeart: '💖',
    growingHeart: '💗',
    beatingHeart: '💓',
    revolvingHearts: '💞',
    twoHearts: '💕',
    heartDecoration: '💟',
    heartWithArrow: '💘',
    heartWithRibbon: '💝',
    brokenHeart: '💔',
    fireHeart: '❤️‍🔥',
    mendingHeart: '❤️‍🩹'
  };

  const handleCopy = (emoji: string) => {
    navigator.clipboard.writeText(emoji);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What does the red heart emoji mean?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The red heart emoji ❤️ is the classic symbol of love, affection, and romance. It's used to express deep love, passion, and strong emotional connections."
        }
      },
      {
        "@type": "Question",
        "name": "What do different colored hearts mean?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Different heart colors convey various meanings: Red ❤️ for love, Orange 🧡 for friendship, Yellow 💛 for happiness, Green 💚 for nature/health, Blue 💙 for trust/stability, Purple 💜 for luxury/royalty, Black 🖤 for complex feelings."
        }
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Heart Emoji ❤️ Complete Guide & Meanings [{currentYear}] 💖</title>
        <meta name="description" content={`【COMPLETE GUIDE】 Learn all about heart emojis ❤️ Meanings, colors & examples 💖 Updated for ${currentYear} ✨ Copy & paste all heart emojis 💝 Perfect for expressing love & emotions!`} />
        <link rel="canonical" href="https://emojicollection.net/emoji/heart" />
        
        <meta property="og:title" content={`Heart Emoji ❤️ Complete Guide & Meanings [${currentYear}] 💖`} />
        <meta property="og:description" content={`Learn all about heart emojis ❤️ Meanings, colors & examples 💖 Updated for ${currentYear} ✨ Copy & paste all heart emojis!`} />
        <meta property="og:url" content="https://emojicollection.net/emoji/heart" />
        <meta property="og:type" content="article" />
        <meta property="og:image" content="https://images.unsplash.com/photo-1518199266791-5375a83190b7?auto=format&fit=crop&w=1200&h=630&q=80" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`Heart Emoji ❤️ Complete Guide & Meanings [${currentYear}] 💖`} />
        <meta name="twitter:description" content={`Learn all about heart emojis ❤️ Meanings, colors & examples 💖 Updated for ${currentYear} ✨ Copy & paste all heart emojis!`} />
        <meta name="twitter:image" content="https://images.unsplash.com/photo-1518199266791-5375a83190b7?auto=format&fit=crop&w=1200&h=630&q=80" />
        
        <meta name="keywords" content="heart emoji, love emoji, heart symbol, colored hearts, heart emoji meaning, heart emoji copy paste, love symbols" />
        
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors"
          >
            <ArrowLeft className="mr-2" size={20} />
            Back to Home
          </button>

          <article className="bg-white rounded-2xl shadow-lg p-8">
            <header className="text-center mb-8">
              <img 
                src="https://images.unsplash.com/photo-1518199266791-5375a83190b7?auto=format&fit=crop&w=1200&q=80"
                alt="Heart emojis expressing love and emotions"
                className="w-full h-48 object-cover rounded-xl mb-8"
              />
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                Heart Emoji ❤️ Complete Guide & Meanings [{currentYear}]
              </h1>
              <p className="text-lg text-gray-600 mb-8">
                Discover everything about heart emojis - from their meanings and colors to platform differences. Updated for {currentYear} with the latest designs and examples.
              </p>
            </header>

            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-6">All Heart Emojis</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(heartEmojis).map(([key, emoji]) => (
                  <div key={key} className="bg-gray-50 rounded-lg p-4">
                    <div className="text-center mb-2">
                      <span className="text-4xl">{emoji}</span>
                    </div>
                    <button
                      onClick={() => handleCopy(emoji)}
                      className={`w-full inline-flex items-center justify-center px-3 py-2 rounded text-sm font-medium ${
                        copied ? 'bg-green-500 text-white' : 'bg-pink-500 text-white hover:bg-pink-600'
                      } transition-colors`}
                    >
                      {copied ? (
                        <>
                          <Check className="mr-1" size={14} />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="mr-1" size={14} />
                          Copy
                        </>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </section>

            <section className="prose prose-lg max-w-none">
              <h2>Heart Colors & Meanings</h2>
              <p>
                Each heart color carries its own symbolic meaning:
              </p>
              <ul>
                <li><strong>Red Heart ❤️</strong> - Classic love, passion, romance</li>
                <li><strong>Orange Heart 🧡</strong> - Friendship, warmth, enthusiasm</li>
                <li><strong>Yellow Heart 💛</strong> - Happiness, positivity, friendship</li>
                <li><strong>Green Heart 💚</strong> - Nature, health, growth, luck</li>
                <li><strong>Blue Heart 💙</strong> - Trust, stability, depth, loyalty</li>
                <li><strong>Purple Heart 💜</strong> - Royalty, luxury, spirituality</li>
                <li><strong>Brown Heart 🤎</strong> - Comfort, earthiness, sincerity</li>
                <li><strong>Black Heart 🖤</strong> - Complexity, rebellion, sophistication</li>
                <li><strong>White Heart 🤍</strong> - Purity, innocence, simplicity</li>
              </ul>

              <h2>Special Heart Variations</h2>
              <p>
                Beyond basic colors, there are special heart emojis for specific expressions:
              </p>
              <ul>
                <li><strong>Sparkling Heart 💖</strong> - Excitement, admiration, sparkle</li>
                <li><strong>Growing Heart 💗</strong> - Growing love, increasing affection</li>
                <li><strong>Beating Heart 💓</strong> - Life, vitality, strong emotions</li>
                <li><strong>Two Hearts 💕</strong> - Mutual love, relationship, friendship</li>
                <li><strong>Heart with Arrow 💘</strong> - Love at first sight, Cupid's arrow</li>
                <li><strong>Heart with Ribbon 💝</strong> - Gift of love, special offering</li>
                <li><strong>Broken Heart 💔</strong> - Heartbreak, sadness, loss</li>
                <li><strong>Fire Heart ❤️‍🔥</strong> - Passionate love, burning desire</li>
                <li><strong>Mending Heart ❤️‍🩹</strong> - Healing, recovery, hope</li>
              </ul>

              <h2>Platform Differences</h2>
              <p>
                Heart emojis may appear differently across platforms:
              </p>
              <ul>
                <li><strong>Apple:</strong> Glossy, dimensional hearts with gradient effects</li>
                <li><strong>Google:</strong> Flatter design with bold colors</li>
                <li><strong>Samsung:</strong> Shiny hearts with 3D appearance</li>
                <li><strong>Microsoft:</strong> Simple, clean heart designs</li>
              </ul>

              <h2>Common Uses</h2>
              <p>
                Heart emojis serve various purposes in digital communication:
              </p>
              <ul>
                <li>Expressing love and affection</li>
                <li>Showing appreciation and gratitude</li>
                <li>Decorating messages and posts</li>
                <li>Emphasizing emotional content</li>
                <li>Creating visual patterns and designs</li>
              </ul>

              <h2>Best Practices</h2>
              <p>
                To use heart emojis effectively:
              </p>
              <ul>
                <li>Choose colors appropriate to your message</li>
                <li>Consider cultural meanings and context</li>
                <li>Combine multiple hearts for emphasis</li>
                <li>Use special variations for specific emotions</li>
              </ul>
            </section>

            <section className="border-t border-gray-200 mt-8 pt-8">
              <h2 className="text-2xl font-semibold mb-4">❓ Frequently Asked Questions</h2>
              <div className="space-y-4">
                <details className="group bg-gray-50 rounded-lg">
                  <summary className="cursor-pointer p-4 font-medium">What does the red heart emoji mean?</summary>
                  <div className="p-4 pt-0 text-gray-600">
                    The red heart emoji ❤️ is the classic symbol of love, affection, and romance. It's used to express deep love, passion, and strong emotional connections.
                  </div>
                </details>
                <details className="group bg-gray-50 rounded-lg">
                  <summary className="cursor-pointer p-4 font-medium">What do different colored hearts mean?</summary>
                  <div className="p-4 pt-0 text-gray-600">
                    Different heart colors convey various meanings: Red ❤️ for love, Orange 🧡 for friendship, Yellow 💛 for happiness, Green 💚 for nature/health, Blue 💙 for trust/stability, Purple 💜 for luxury/royalty, Black 🖤 for complex feelings.
                  </div>
                </details>
                <details className="group bg-gray-50 rounded-lg">
                  <summary className="cursor-pointer p-4 font-medium">When should I use special heart variations?</summary>
                  <div className="p-4 pt-0 text-gray-600">
                    Special heart variations like 💖 (sparkling), 💘 (with arrow), or 💝 (with ribbon) are perfect for specific occasions or emotions. Use them to add extra meaning or emphasis to your messages.
                  </div>
                </details>
              </div>
            </section>
          </article>
        </div>
      </div>
    </>
  );
};

export default HeartEmoji;